import { useState } from 'react';

export type SocialPlatform = 
  | 'instagram-feed' | 'instagram-story' | 'instagram-reel'
  | 'facebook-post' | 'facebook-cover' | 'facebook-ad'
  | 'twitter-post' | 'twitter-header'
  | 'linkedin-post' | 'linkedin-banner'
  | 'youtube-thumbnail' | 'youtube-banner'
  | 'pinterest-pin';

export interface PlatformPreset {
  platform: SocialPlatform;
  width: number;
  height: number;
  safeZone?: { top: number; right: number; bottom: number; left: number };
  name: string;
}

export const PLATFORM_PRESETS: PlatformPreset[] = [
  { platform: 'instagram-feed', width: 1080, height: 1080, name: 'Instagram Feed (Square)' },
  { platform: 'instagram-story', width: 1080, height: 1920, name: 'Instagram Story' },
  { platform: 'instagram-reel', width: 1080, height: 1920, name: 'Instagram Reel' },
  { platform: 'facebook-post', width: 1200, height: 630, name: 'Facebook Post' },
  { platform: 'facebook-cover', width: 820, height: 312, name: 'Facebook Cover', safeZone: { top: 24, right: 24, bottom: 24, left: 24 } },
  { platform: 'facebook-ad', width: 1200, height: 628, name: 'Facebook Ad' },
  { platform: 'twitter-post', width: 1200, height: 675, name: 'Twitter Post' },
  { platform: 'twitter-header', width: 1500, height: 500, name: 'Twitter Header', safeZone: { top: 30, right: 30, bottom: 30, left: 30 } },
  { platform: 'linkedin-post', width: 1200, height: 627, name: 'LinkedIn Post' },
  { platform: 'linkedin-banner', width: 1584, height: 396, name: 'LinkedIn Banner', safeZone: { top: 20, right: 20, bottom: 20, left: 20 } },
  { platform: 'youtube-thumbnail', width: 1280, height: 720, name: 'YouTube Thumbnail' },
  { platform: 'youtube-banner', width: 2560, height: 1440, name: 'YouTube Banner', safeZone: { top: 345, right: 500, bottom: 345, left: 500 } },
  { platform: 'pinterest-pin', width: 1000, height: 1500, name: 'Pinterest Pin' },
];

interface OptimizedImage {
  id: string;
  original: string;
  optimized: string;
  platform: SocialPlatform;
  preset: PlatformPreset;
}

export const useSocialMediaOptimizer = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [optimizedImages, setOptimizedImages] = useState<OptimizedImage[]>([]);

  const optimizeForPlatform = async (
    imageUrl: string,
    imageId: string,
    preset: PlatformPreset,
    showSafeZone: boolean = false
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          canvas.width = preset.width;
          canvas.height = preset.height;

          // Calculate scaling to cover the canvas
          const scale = Math.max(
            preset.width / img.width,
            preset.height / img.height
          );

          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;

          // Center the image
          const x = (preset.width - scaledWidth) / 2;
          const y = (preset.height - scaledHeight) / 2;

          // Fill with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw image
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

          // Draw safe zone guides if requested
          if (showSafeZone && preset.safeZone) {
            ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
            ctx.lineWidth = 2;
            ctx.setLineDash([10, 5]);
            ctx.strokeRect(
              preset.safeZone.left,
              preset.safeZone.top,
              preset.width - preset.safeZone.left - preset.safeZone.right,
              preset.height - preset.safeZone.top - preset.safeZone.bottom
            );
            
            // Add label
            ctx.font = '16px Arial';
            ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
            ctx.fillText('Safe Zone', preset.safeZone.left + 10, preset.safeZone.top + 25);
          }

          resolve(canvas.toDataURL('image/jpeg', 0.9));
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const optimizeForPlatforms = async (
    image: { id: string; preview: string },
    platforms: SocialPlatform[],
    showSafeZone: boolean = false
  ) => {
    setIsProcessing(true);
    setProgress(0);
    const results: OptimizedImage[] = [];

    for (let i = 0; i < platforms.length; i++) {
      const preset = PLATFORM_PRESETS.find(p => p.platform === platforms[i]);
      if (!preset) continue;

      try {
        const optimized = await optimizeForPlatform(image.preview, image.id, preset, showSafeZone);
        results.push({
          id: `${image.id}-${preset.platform}`,
          original: image.preview,
          optimized,
          platform: preset.platform,
          preset,
        });
      } catch (error) {
        console.error(`Failed to optimize for ${preset.platform}:`, error);
      }
      setProgress(((i + 1) / platforms.length) * 100);
    }

    setOptimizedImages(results);
    setIsProcessing(false);
    return results;
  };

  const downloadOptimized = (image: OptimizedImage, filename: string) => {
    const link = document.createElement('a');
    link.href = image.optimized;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = (images: OptimizedImage[], baseFilename: string) => {
    images.forEach((image) => {
      const filename = `${baseFilename}_${image.platform}.jpg`;
      downloadOptimized(image, filename);
    });
  };

  return {
    isProcessing,
    progress,
    optimizedImages,
    optimizeForPlatforms,
    downloadOptimized,
    downloadAll,
    PLATFORM_PRESETS,
  };
};
